import { useState } from 'react'

import { useAuth } from '@forbiz/web-kit'

import { ordersApi } from '../lib/orders.js'
import { triggerDownload, toQuery } from '../lib/download.js'
import { useAsync } from '../lib/useAsync.js'
import styles from '../app.module.css'

/**
 * 통계 탭 (BIZ-46 → 2축 BIZ-91). 매일 보내는 담당자보다 총무팀장·관리자가 예산·관계 관리에 쓰는
 * '돌아보기' 화면. 기간(희망일) 토글 + 집계 카드 + 거래처별·수신자별 표(축 토글). 차트는 범위 밖.
 * 집계·필드 노출은 서버가 기업 스코프로 건다 — 실제 화환 업체는 내려오지 않는다.
 */

/** 기간 토글. 희망일(desired_date) 기준. */
const PERIODS: { value: string; label: string }[] = [
  { value: 'thisMonth', label: '이번 달' },
  { value: 'thisQuarter', label: '이번 분기' },
  { value: 'thisYear', label: '올해' },
]

function isoDate(d: Date): string {
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${mm}-${dd}`
}

/** 기간 토글 → 희망일 범위(from·to). */
function periodRange(period: string): { from: string; to: string } {
  const now = new Date()
  const y = now.getFullYear()
  const m = now.getMonth()
  if (period === 'thisQuarter') {
    const qs = Math.floor(m / 3) * 3
    return { from: isoDate(new Date(y, qs, 1)), to: isoDate(new Date(y, qs + 3, 0)) }
  }
  if (period === 'thisYear') {
    return { from: isoDate(new Date(y, 0, 1)), to: isoDate(new Date(y, 11, 31)) }
  }
  return { from: isoDate(new Date(y, m, 1)), to: isoDate(new Date(y, m + 1, 0)) }
}

const fmtNum = (n: number | undefined) => (n === undefined ? '—' : n.toLocaleString('ko-KR'))
const fmtWon = (n: number | undefined) => (n === undefined ? '—' : `${n.toLocaleString('ko-KR')}원`)

export function StatsPage() {
  const { user } = useAuth()
  const isCompany = user?.role === 'COMPANY_ADMIN' || user?.role === 'COMPANY_MEMBER'
  const isAdmin = user?.role === 'COMPANY_ADMIN'

  const [period, setPeriod] = useState('thisMonth')
  const [axis, setAxis] = useState<'partner' | 'recipient'>('partner')
  const { from, to } = periodRange(period)
  const { data, loading, error } = useAsync(() => ordersApi.stats({ from, to }), [from, to])

  if (!isCompany) {
    return (
      <main>
        <div className={styles.header}>
          <h1 className={styles.title}>리포트</h1>
        </div>
        <p className={styles.muted}>기업 담당자만 볼 수 있는 화면입니다.</p>
      </main>
    )
  }

  const totals = data?.totals
  const rows = (axis === 'partner' ? data?.byPartner : data?.byRecipient) ?? []
  // 막대는 금액 비례. 건수는 축에 따라 대부분 1(수신자별)이라 폭 기준으로 못 쓴다.
  const maxAmount = rows.reduce((m, r) => Math.max(m, r.amountSum), 0)
  const axisUnit = axis === 'partner' ? '곳' : '명'

  const periodLabel = PERIODS.find((p) => p.value === period)?.label ?? '이번 달'

  return (
    <main>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>리포트</h1>
          <p className={styles.subtitle}>기간별 주문 집계와 거래처·수신자별 현황을 돌아봅니다</p>
        </div>
        <div className={styles.headerActions}>
          <div className={styles.segmented} role="group" aria-label="기간 선택">
            {PERIODS.map((p) => (
              <button
                key={p.value}
                type="button"
                aria-pressed={period === p.value}
                className={period === p.value ? `${styles.segmentBtn} ${styles.segmentOn}` : styles.segmentBtn}
                onClick={() => setPeriod(p.value)}
              >
                {p.label}
              </button>
            ))}
          </div>
          {isAdmin ? (
            <button
              data-variant="secondary"
              className={styles.hideMobile}
              onClick={() => triggerDownload(`/api/orders/stats/export${toQuery({ from, to })}`)}
            >
              ⤓ 엑셀 내보내기
            </button>
          ) : null}
        </div>
      </div>

      {error ? (
        <div className={styles.error} role="alert">
          {error}
        </div>
      ) : (
        <div className={styles.reportCard}>
          {/* 요약 스트립 — 금액이 주인공, 보조 지표는 작게(확인 필요는 앰버). */}
          <div className={styles.summaryStrip}>
            <div className={styles.summaryLabel}>{periodLabel} 금액 합계</div>
            <div className={styles.summaryAmount}>{fmtWon(totals?.amountSum)}</div>
            <div className={styles.summaryStats}>
              <div className={styles.summaryStat}>
                <div className={styles.summaryStatNum}>{fmtNum(totals?.count)}</div>
                <div className={styles.summaryStatLbl}>주문 수</div>
              </div>
              <div className={styles.summaryStat}>
                <div className={styles.summaryStatNum}>{fmtNum(totals?.inProgress)}</div>
                <div className={styles.summaryStatLbl}>진행 중</div>
              </div>
              <div className={styles.summaryStat}>
                <div className={`${styles.summaryStatNum} ${styles.summaryStatWarn}`}>{fmtNum(totals?.attention)}</div>
                <div className={styles.summaryStatLbl}>확인 필요</div>
              </div>
            </div>
          </div>

          <div className={styles.reportDivider} aria-hidden="true" />

          {/* 랭킹 — 거래처별/수신자별 */}
          <div className={styles.reportRanks}>
            <div className={styles.statSectionHead}>
              <div className={styles.segmented} role="group" aria-label="집계 기준">
                <button
                  type="button"
                  aria-pressed={axis === 'partner'}
                  className={axis === 'partner' ? `${styles.segmentBtn} ${styles.segmentOn}` : styles.segmentBtn}
                  onClick={() => setAxis('partner')}
                >
                  거래처별
                </button>
                <button
                  type="button"
                  aria-pressed={axis === 'recipient'}
                  className={axis === 'recipient' ? `${styles.segmentBtn} ${styles.segmentOn}` : styles.segmentBtn}
                  onClick={() => setAxis('recipient')}
                >
                  수신자별
                </button>
              </div>
              {rows.length > 0 ? (
                <span className={styles.muted}>{fmtNum(rows.length)}{axisUnit} · 상위순</span>
              ) : null}
            </div>
            {loading ? (
              <p className={styles.muted}>불러오는 중…</p>
            ) : rows.length === 0 ? (
              <div className={styles.empty}>이 기간에 집계할 주문이 없습니다</div>
            ) : (
              <ol className={styles.rankList}>
                {rows.map((r, i) => {
                  const pct = maxAmount > 0 ? Math.max(4, Math.round((r.amountSum / maxAmount) * 100)) : 0
                  return (
                    <li key={r.label} className={styles.rankRow}>
                      <span
                        className={styles.rankNo}
                        data-top={i < 3 ? 'y' : undefined}
                        aria-hidden="true"
                      >
                        {i + 1}
                      </span>
                      <div className={styles.rankBody}>
                        <div className={styles.rankLine}>
                          <span className={styles.rankName} title={r.label}>
                            {r.label}
                          </span>
                          <span className={styles.rankStat}>
                            <b>{fmtNum(r.count)}</b>건 · {fmtWon(r.amountSum)}
                          </span>
                        </div>
                        <div className={styles.rankTrack}>
                          <div className={styles.rankFill} style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    </li>
                  )
                })}
              </ol>
            )}
          </div>
        </div>
      )}
    </main>
  )
}
